<template>
  <div class="page-container">
    <!-- Left Sidebar Navigation -->
    <aside class="sidebar" :class="{ 'collapsed': isSidebarCollapsed }">
      <button class="sidebar-toggle" @click="toggleSidebar">
        <i :class="isSidebarCollapsed ? 'fas fa-bars' : 'fas fa-times'"></i>
      </button>
      <div class="logo-container">
        <img src="@/assets/tartoro-icon.png" alt="Tartoro Logo" class="logo" />
        <span class="logo-text" v-if="!isSidebarCollapsed">Tartoro</span>
      </div>

      <div class="nav-section">
        <h3 v-if="!isSidebarCollapsed">{{ $t('customerHome.nav.informationManagement') }}</h3>
        <nav>
          <router-link to="/dashboard" class="nav-item" active-class="active">
            <i class="fas fa-tachometer-alt"></i>
            <span v-if="!isSidebarCollapsed">{{ $t('customerHome.nav.dashboard') }}</span>
          </router-link>
          <router-link to="/business-details" class="nav-item" active-class="active">
            <i class="fas fa-briefcase"></i>
            <span v-if="!isSidebarCollapsed">{{ $t('customerHome.nav.businessDetails') }}</span>
          </router-link>
          <router-link to="/documents" class="nav-item" active-class="active">
            <i class="fas fa-folder-open"></i>
            <span v-if="!isSidebarCollapsed">{{ $t('customerHome.nav.documents') }}</span>
          </router-link>
        </nav>
      </div>

      <div class="nav-section">
        <h3 v-if="!isSidebarCollapsed">{{ $t('customerHome.nav.companyManagement') }}</h3>
        <nav>
          <router-link to="/mail" class="nav-item" active-class="active">
            <i class="fas fa-inbox"></i>
            <span v-if="!isSidebarCollapsed">{{ $t('customerHome.nav.mail') }}</span>
          </router-link>
        </nav>
      </div>

      <div class="nav-section">
        <h3 v-if="!isSidebarCollapsed">{{ $t('customerHome.nav.accountManagement') }}</h3>
        <nav>
          <router-link to="/users" class="nav-item" active-class="active">
            <i class="fas fa-user-friends"></i>
            <span v-if="!isSidebarCollapsed">{{ $t('customerHome.nav.users') }}</span>
          </router-link>
          <router-link to="/settings" class="nav-item" active-class="active">
            <i class="fas fa-cog"></i>
            <span v-if="!isSidebarCollapsed">{{ $t('customerHome.nav.settings') }}</span>
          </router-link>
        </nav>
      </div>

      <div class="user-footer">
        <div class="user-info-footer" v-if="!isSidebarCollapsed">
          <img :src="user.avatar" alt="User Avatar" class="user-avatar-sm" />
          <div class="user-details">
            <span class="user-name-sm">{{ user.name }}</span>
          </div>
        </div>
        <button class="share-btn" @click="handleLogout" :title="$t('customerHome.user.logout')">
          <i class="fas fa-sign-out-alt"></i>
        </button>
      </div>
    </aside>

    <!-- Main Content Area -->
    <main class="main-content" :class="{ 'expanded': isSidebarCollapsed }">
      <!-- Header -->
      <header class="header">
        <div class="home-header">
          <div class="user-info">
            <h3 class="user-header-name">{{ user.name }}</h3>
            <div class="business-dropdown">
              <div class="business-selector" @click="toggleBusinessDropdown">
                <i class="fas fa-building"></i>
                <span id="selected-business">{{ selectedBusiness?.name || $t('customerHome.header.selectBusiness') }}</span>
                <i class="fas fa-chevron-down"></i>
              </div>
              <div class="dropdown-content" v-show="isBusinessDropdownOpen">
                <a href="#" 
                   v-for="business in userBusinesses" 
                   :key="business.id" 
                   @click.prevent="selectBusiness(business)"
                   :class="{ 'active': selectedBusiness?.id === business.id }">
                  <i class="fas fa-building"></i>
                  {{ business.name }}
                </a>
                <div v-if="!userBusinesses.length" class="no-businesses">
                  {{ $t('customerHome.header.noBusinesses') }}
                </div>
              </div>
            </div>
            <div class="language-dropdown">
              <div class="language-selector" @click="toggleDropdown">
                <span class="globe-icon">🌐</span>
                <span id="selected-language">{{ currentLanguage }}</span> ▼
              </div>
              <div class="dropdown-content" v-show="isDropdownOpen">
                <a href="#" @click.prevent="changeLanguage('en')">English</a>
                <a href="#" @click.prevent="changeLanguage('es')">Español</a>
              </div>
            </div>
          </div>
        </div>
      </header>

      <!-- Content Area -->
      <div class="content-area">
        <router-view :selected-business="selectedBusiness"></router-view>
      </div>
    </main>
  </div>
</template>

<script>
import '@/styles/CustomerHome.css'
import authService from '@/services/authService'

export default {
  name: 'CustomerHome',
  data() {
    return {
      user: {
        name: '',
        role: 'Business owner',
        id: '',
        avatar: require('@/assets/avatar.png')
      },
      isSidebarCollapsed: false,
      isDropdownOpen: false,
      isBusinessDropdownOpen: false,
      currentLanguage: 'English',
      loading: false,
      userBusinesses: [],
      selectedBusiness: null,
      navItems: [
        { name: 'Dashboard', path: '/dashboard', icon: 'fas fa-home', label: 'customerHome.nav.dashboard' },
        { name: 'BusinessDetails', path: '/business-details', icon: 'fas fa-building', label: 'customerHome.nav.businessDetails' },
        { name: 'Documents', path: '/documents', icon: 'fas fa-file-alt', label: 'customerHome.nav.documents' },
        { name: 'Mail', path: '/mail', icon: 'fas fa-envelope', label: 'customerHome.nav.mail' },
        { name: 'Users', path: '/users', icon: 'fas fa-users', label: 'customerHome.nav.users' },
        { name: 'Settings', path: '/settings', icon: 'fas fa-cog', label: 'customerHome.nav.settings' }
      ]
    }
  },
  methods: {
    toggleSidebar() {
      this.isSidebarCollapsed = !this.isSidebarCollapsed
    },
    navigateToDashboard() {
      // check if customerId is in the query if not then load form local storage
      if (!this.$route.query.customerId) {
        this.$route.query.customerId = localStorage.getItem('customerId');
      }
      this.$router.push({ 
        path: '/dashboard',
        query: { customerId: this.$route.query.customerId }
      });
    },
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    changeLanguage(lang) {
      this.currentLanguage = lang === 'en' ? 'English' : 'Español';
      this.$i18n.locale = lang;
      localStorage.setItem('language', lang);
      this.isDropdownOpen = false;
    },
    toggleBusinessDropdown() {
      this.isBusinessDropdownOpen = !this.isBusinessDropdownOpen;
    },
    selectBusiness(business) {
      this.selectedBusiness = business;
      this.isBusinessDropdownOpen = false;
      
      // Update the current route with the selected business
      this.$router.push({
        name: this.$route.name,
        params: { businessId: business.id },
        query: { ...this.$route.query }
      });

      // Emit event for child components
      this.$emit('business-selected', business);
    },
    async loadUserData() {
      try {
        // First check if we have customer data in localStorage from OTP verification
        const storedCustomerData = localStorage.getItem('customerData');
        
        if (storedCustomerData) {
          try {
            const customerData = JSON.parse(storedCustomerData);
            if (customerData && customerData.firstName && customerData.lastName) {
              console.log('CustomerHome - Using stored customer data from localStorage');
              this.user = {
                name: `${customerData.firstName} ${customerData.lastName}`,
                role: 'Business owner',
                id: customerData.id,
                avatar: require('@/assets/avatar.png')
              };
              console.log('CustomerHome - User data set from localStorage:', this.user);
              
              // Load user businesses after getting user data
              await this.loadUserBusinesses();
              return; // Exit early as we have the data
            }
          } catch (parseError) {
            console.error('CustomerHome - Error parsing stored customer data:', parseError);
            // Continue with API call if there's an error parsing local data
          }
        }
        
        // If no stored data or parsing failed, get customer ID from localStorage
        const customerId = localStorage.getItem('customerId');
        console.log('CustomerHome - Customer ID from localStorage:', customerId);
        
        if (!customerId) {
          console.error('CustomerHome - No customer ID found in localStorage');
          return;
        }

        this.loading = true;
        console.log('CustomerHome - Fetching customer details...');
        const response = await authService.getCustomerDetails(customerId);
        console.log('CustomerHome - Customer details response:', response);

        if (response && response.success && response.customer) {
          this.user = {
            name: `${response.customer.firstName} ${response.customer.lastName}`,
            role: 'Business owner',
            id: response.customer.id,
            avatar: require('@/assets/avatar.png')
          };
          console.log('CustomerHome - User data set from API:', this.user);
          
          // Also update the stored customerData
          localStorage.setItem('customerData', JSON.stringify(response.customer));
          
          // Load user businesses after getting user data
          await this.loadUserBusinesses();
        } else {
          throw new Error(response?.message || 'Failed to load customer data');
        }
      } catch (error) {
        console.error('CustomerHome - Error loading customer data:', error);
        // Set default user data in case of error
        this.user = {
          name: 'User',
          role: 'Business owner',
          id: localStorage.getItem('customerId'),
          avatar: require('@/assets/avatar.png')
        };
        // Still try to load businesses even if user data fails
        await this.loadUserBusinesses();
      } finally {
        this.loading = false;
      }
    },
    async loadUserBusinesses() {
      try {
        const customerId = localStorage.getItem('customerId');
        console.log('CustomerHome - Loading businesses for customer ID:', customerId);
        
        if (!customerId) {
          console.error('CustomerHome - No customer ID found for loading businesses');
          return;
        }

        // First try to get from localStorage
        const storedBusinesses = localStorage.getItem('userBusinesses');
        console.log('CustomerHome - Stored businesses from localStorage:', storedBusinesses);
        
        if (storedBusinesses) {
          this.userBusinesses = JSON.parse(storedBusinesses);
          console.log('CustomerHome - Parsed businesses from localStorage:', this.userBusinesses);
        } else {
          // If not in localStorage, fetch from API
          console.log('CustomerHome - Fetching businesses from API...');
          const response = await authService.getCustomerBusinesses(customerId);
          console.log('CustomerHome - API response for businesses:', response);
          
          if (response && response.success && Array.isArray(response.businesses)) {
            this.userBusinesses = response.businesses;
            console.log('CustomerHome - Setting businesses from API:', this.userBusinesses);
            localStorage.setItem('userBusinesses', JSON.stringify(response.businesses));
          } else {
            console.error('CustomerHome - Invalid response format:', response);
            throw new Error(response?.error || 'Invalid response format from server');
          }
        }
        
        // Set initial selected business
        if (this.userBusinesses && this.userBusinesses.length > 0) {
          console.log('CustomerHome - Setting initial selected business from', this.userBusinesses.length, 'businesses');
          const routeBusinessId = this.$route.params.businessId;
          if (routeBusinessId) {
            const business = this.userBusinesses.find(b => b.id === routeBusinessId);
            this.selectedBusiness = business || this.userBusinesses[0];
          } else {
            this.selectedBusiness = this.userBusinesses[0];
            // Update route with business ID if not present
            if (this.$route.name !== 'CustomerHome') {
              this.$router.push({ 
                name: this.$route.name, 
                params: { businessId: this.selectedBusiness.id }
              });
            }
          }
          console.log('CustomerHome - Selected business set to:', this.selectedBusiness);
        } else {
          console.log('CustomerHome - No businesses available to select from');
          this.userBusinesses = [];
          this.selectedBusiness = null;
        }
      } catch (error) {
        console.error('CustomerHome - Error loading businesses:', error);
        console.error('CustomerHome - Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        this.userBusinesses = [];
        this.selectedBusiness = null;
      }
    },
    async handleLogout() {
      try {
        await authService.logout();
        // Clear local storage
        localStorage.removeItem('customerToken');
        localStorage.removeItem('customerData');
        localStorage.removeItem('customerId');
        // Redirect to sign in page
        this.$router.push('/customer-signin');
      } catch (error) {
        console.error('Error during logout:', error);
      }
    },
    handleBusinessChange() {
      if (this.selectedBusiness) {
        // Update route with new business ID
        this.$router.push({ 
          name: this.$route.name, 
          params: { businessId: this.selectedBusiness.id }
        });
      }
    }
  },
  created() {
    // Set initial language from localStorage or default to 'en'
    const savedLanguage = localStorage.getItem('language') || 'en';
    this.$i18n.locale = savedLanguage;
    this.currentLanguage = savedLanguage === 'en' ? 'English' : 'Español';
    
    // Load user data
    this.loadUserData();
  },

  mounted() {
    // Add Font Awesome CSS
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css'
    document.head.appendChild(link)
  },
  watch: {
    // Watch for route changes to update selected business
    '$route.params.businessId': {
      immediate: true,
      handler(newBusinessId) {
        if (newBusinessId && this.userBusinesses.length > 0) {
          const business = this.userBusinesses.find(b => b.id === newBusinessId);
          if (business) {
            this.selectedBusiness = business;
          }
        }
      }
    }
  }
}
</script>

<style scoped>
.business-dropdown {
  position: relative;
  margin: 0 15px;
}

.business-selector {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 4px;
  cursor: pointer;
  min-width: 200px;
  transition: all 0.3s ease;
}

.business-selector:hover {
  background: #e9e9e9;
}

.business-selector i {
  margin-right: 8px;
  color: #666;
}

.business-selector span {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-content {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
}

.dropdown-content a {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  color: #333;
  text-decoration: none;
  transition: all 0.2s ease;
}

.dropdown-content a:hover {
  background: #f5f5f5;
}

.dropdown-content a.active {
  background: #e3f2fd;
  color: #1976d2;
}

.dropdown-content a i {
  margin-right: 8px;
  color: #666;
}

.no-businesses {
  padding: 12px;
  color: #666;
  text-align: center;
  font-style: italic;
}

/* Add responsive styles */
@media (max-width: 768px) {
  .business-selector {
    min-width: 150px;
  }
}
</style>